import { capitalCase } from 'change-case';
import debounce from 'lodash.debounce';
import React from 'react';
import { DateRange, guid, UTC } from 'rewire-common';
import { observable } from 'rewire-core';
import { createColumn, createGrid, ICell, IColumn, IGrid, IGridOptions, IRowData } from 'rewire-grid';
import { ErrorSeverity, Form, field, IError, IFormContext, IValidationFn, validator } from 'rewire-ui';
import {
  IMessage,
  INameEntity,
  ITimeOffRange,
  ITimeOffRequest,
  ITimeOffType,
  ITimeOffTypeSummary,
  ITimeOffTypeTransaction,
  TimeOffTypeSummary,
} from '../DataTypes';
import DataQueries from '../graphql/Queries';
import mainPageModel from '../main-navigation/MainPageModel';
import { payRouteManager, timeOffCalendarRouteManager } from '../main-navigation/RouteManager';
import Routes from '../Routes';
import accrualDialogModel from '../settings/AccrualDialogModel';
import { TimeOffTypeBalanceCell } from '../settings/TimeOffBalancesView';
import MessagesModel from '../timecard/MessagesModel';
import infoPanelTimeOffRequestViewModel, {
  dialogInfoPanelTimeOffRequestViewModel,
} from '../timeoff/InfoPanelTimeOffRequestViewModel';
import timeOffDialogModel, { timeOffRequestReturnSchema } from '../timeoff/TimeOffDialogModel';
import ActionCell from '../ui/ActionCell';
import CellRendererSelector from '../ui/CellRendererSelector';
import LinkCell from '../ui/LinkCell';
import { roundTo } from '../utility/DataConverters';
import { localTimeZoneUTCNow } from '../utility/DateUtils';
import SelectedRowWatcher from '../utility/SelectedRowWatcher';

interface ITimeOffBalancesModelState {
  timeOffTypes: ITimeOffType[];
  timeOffRequests: ITimeOffRequest[];
  timeOffTypeSummaries: ITimeOffTypeSummary[];
  timeOffTypesGrid: IGrid;
  transactionsGrid: IGrid;
  upcomingTimeOffRequestsGrid: IGrid;
  pastTimeOffRequestsGrid: IGrid;
  selectedTimeOffType?: ITimeOffTypeSummary;
}

function safeCapitalCase(text: string) {
  return !text ? '' : capitalCase(text);
}

function getEffectiveRange(cell: ICell): DateRange {
  const earnedDate = cell.row.data.earnedDate as UTC | undefined;
  if (earnedDate) {
    return new DateRange(earnedDate, earnedDate);
  }
  return cell.row.cells.effective.value;
}

function toDateRange(dateRange?: DateRange): string {
  if (!dateRange) return '';
  const start: string = dateRange.startAsString();
  const end: string = dateRange.endAsString();

  return start.localeCompare(end) === 0 ? start : `${start} - ${end}`;
}

function toRange(timeOffType?: any): string {
  if (!timeOffType || !timeOffType.effective) return '';
  let start = timeOffType.effective.startAsString();
  const end = timeOffType.effective.endAsString();
  if (timeOffType.earnedDate && timeOffType.source === 'timecard') {
    start = timeOffType.earnedDate.toDateString();
  }

  return start.localeCompare(end) === 0 ? start : `${start} - ${end}`;
}

export class TimeOffBalancesDialogModel {
  state: ITimeOffBalancesModelState;
  selectedRowWatcher: SelectedRowWatcher = new SelectedRowWatcher();
  messagesModel: MessagesModel;
  timeOffBalancesForm: Form;
  employeeId?: string;
  _active: boolean;

  constructor() {
    this.state = observable({
      timeOffTypes: [],
      timeOffTypeSummaries: [],
      timeOffRequests: [],
      timeOffTypesGrid: this.createGrid(this.timeOffTypeColumns),
      transactionsGrid: this.createGrid(this.transactionColumns),
      upcomingTimeOffRequestsGrid: this.createGrid(this.timeOffRequestColumns),
      pastTimeOffRequestsGrid: this.createGrid(this.timeOffRequestColumns),
      selectedTimeOffType: undefined,
    });

    this.timeOffBalancesForm = Form.create(
      (form: IFormContext) => ({
        asOfDate: form.date().label('As Of').updateOnChange().onValueChange(this.handleAsOfDateChange),
      }),
      {
        asOfDate: localTimeZoneUTCNow(),
      },
      { disableErrors: true },
    );

    this.messagesModel = MessagesModel.create();
    this._active = false;
  }

  activate() {
    if (!this._active) {
      this._active = true;
      accrualDialogModel.onEvent.addListener('submit', this.handleAccrualDialogSubmit);
      accrualDialogModel.onEvent.addListener('remove', this.handleAccrualDialogRemove);
      // timeOffDialogModel.onEvent.addListener('submit', this.handleTimeOffDialogSubmit);
      infoPanelTimeOffRequestViewModel.onEvent.addListener('submit', this.handleTimeOffDialogSubmit);
      infoPanelTimeOffRequestViewModel.onEvent.addListener('delete', this.handleDeleteTimeOffRequests);
      dialogInfoPanelTimeOffRequestViewModel.onEvent.addListener('submit', this.handleTimeOffDialogSubmit);
      dialogInfoPanelTimeOffRequestViewModel.onEvent.addListener('delete', this.handleDeleteTimeOffRequests);
    }
  }

  deactivate() {
    if (this._active) {
      this._active = false;
      accrualDialogModel.onEvent.removeListener('submit', this.handleAccrualDialogSubmit);
      accrualDialogModel.onEvent.removeListener('remove', this.handleAccrualDialogRemove);
      // timeOffDialogModel.onEvent.removeListener('submit', this.handleTimeOffDialogSubmit);
      infoPanelTimeOffRequestViewModel.onEvent.removeListener('submit', this.handleTimeOffDialogSubmit);
      infoPanelTimeOffRequestViewModel.onEvent.removeListener('delete', this.handleDeleteTimeOffRequests);
      dialogInfoPanelTimeOffRequestViewModel.onEvent.removeListener('submit', this.handleTimeOffDialogSubmit);
      dialogInfoPanelTimeOffRequestViewModel.onEvent.removeListener('delete', this.handleDeleteTimeOffRequests);
    }
  }

  get timeOffTypesGrid(): IGrid {
    return this.state.timeOffTypesGrid;
  }

  get transactionsGrid(): IGrid {
    return this.state.transactionsGrid;
  }

  get upcomingTimeOffRequestsGrid(): IGrid {
    return this.state.upcomingTimeOffRequestsGrid;
  }

  get pastTimeOffRequestsGrid(): IGrid {
    return this.state.pastTimeOffRequestsGrid;
  }

  get messages(): IMessage[] {
    return this.messagesModel.messages;
  }

  get asof(): UTC | undefined {
    return this.timeOffBalancesForm.field.asOfDate.value;
  }

  set asof(asof: UTC | undefined) {
    this.timeOffBalancesForm.field.asOfDate.value = asof;
  }

  handleAsOfDateChange = async () => {
    this.asof = this.timeOffBalancesForm.field.asOfDate.value;
    await this.loadTimeOffTypes(this.employeeId!, undefined, true);
    requestAnimationFrame(() => this.filterTimeOffRequests());
  };

  get asofField(): any {
    return this.timeOffBalancesForm.field.asOfDate;
  }

  get selectedTimeOffType(): ITimeOffType | undefined {
    return this.state.selectedTimeOffType;
  }

  set selectedTimeOffType(timeOffType: ITimeOffTypeSummary | undefined) {
    this.state.selectedTimeOffType = timeOffType;
  }

  get canViewTimeOffRequests(): boolean {
    return mainPageModel.timeOffRequestAccess;
  }

  loadTimeOffTypes = async (employeeId?: string, reasonId?: string, force = false) => {
    if (!employeeId || !this.asof) {
      this.state.timeOffTypesGrid.rows.set([]);
      this.state.transactionsGrid.rows.set([]);
      return;
    }

    const shouldLoadTimeOffTransactions = this.employeeId !== employeeId;
    let selectedTimeOffTypeId = this.selectedTimeOffType?.id;
    this.employeeId = employeeId;
    this.selectedTimeOffType = undefined;
    if (force || shouldLoadTimeOffTransactions) {
      this.state.timeOffTypes = [];
      this.state.timeOffTypes = await DataQueries.getEmployeeTimeOffTypes(employeeId!, this.asof!.toDate());
    }

    if (this.state.timeOffTypes?.length > 0) {
      this.state.timeOffTypeSummaries = [];
      this.state.timeOffTypeSummaries = this.state.timeOffTypes.map((tot) => new TimeOffTypeSummary(tot));
      const rows = this.createTimeOffTypeRows(this.state.timeOffTypeSummaries, this.state.timeOffTypesGrid.columns);
      this.state.timeOffTypesGrid.set(rows);
      this.addTimeOffTypeErrors();

      this.selectedRowWatcher.watch(this.timeOffTypesGrid, (row, foundRow) => {
        this.selectedTimeOffType = foundRow?.data;
        this.loadTimeOffTransactions(foundRow?.data.id);
        requestAnimationFrame(() => this.filterTimeOffRequests());
      });

      selectedTimeOffTypeId =
        this.state.timeOffTypes.find((f) => f.withdrawalReasons?.some((r) => r.id === reasonId))?.id ?? selectedTimeOffTypeId;
      if (selectedTimeOffTypeId) {
        const row = this.state.timeOffTypesGrid.rows.find((r: any) => r.id === selectedTimeOffTypeId);
        if (row) this.state.timeOffTypesGrid.selectRows([row]);
      }
    }
    if (shouldLoadTimeOffTransactions || force) {
      this.loadTimeOffRequests();
    } else {
      this.updateTimeOffTypeSummariesWithTimeOffRequests();
    }
  };

  loadTimeOffTransactions = async (id: string) => {
    this.transactionsGrid.set([]);
    this.messagesModel.reset();

    const timeOffType: ITimeOffType | undefined = this.state.timeOffTypes.find((tot) => tot.id === id);
    if (!timeOffType) return;

    this.loadTransactionErrors(timeOffType);

    const rows = this.createTransactionRows(timeOffType.transactions, this.state.transactionsGrid.columns);
    this.state.transactionsGrid.set(rows);
  };

  loadTransactionErrors(timeOffType: ITimeOffType) {
    for (const message of timeOffType.messages || []) {
      this.messagesModel.add(message);
    }
    for (const transaction of timeOffType.transactions || []) {
      if (transaction.messages) {
        for (const message of transaction.messages) {
          this.messagesModel.add(message);
          this.addTransactionError(message);
        }
      }
    }
  }

  loadTimeOffRequests = async () => {
    if (!this.employeeId || !this.asof) {
      return;
    }

    const result = await DataQueries.getTimeOffRequestsForTimeOffBalances(timeOffRequestReturnSchema, new DateRange(), {
      employeeIds: [this.employeeId],
    });
    const timeOffRequests = result?.data as ITimeOffRequest[];
    this.state.timeOffRequests = this.deDupe(timeOffRequests);
    this.updateTimeOffTypeSummariesWithTimeOffRequests();
  };

  updateTimeOffTypeSummariesWithTimeOffRequests() {
    this.resetTimeOffTypeSummaries();
    for (const tor of this.state.timeOffRequests) {
      for (const rng of tor.timeOffRanges) {
        const timeOffType: ITimeOffType | undefined = this.findTimeOffTypeForReasonId(rng.reason.id);
        if (timeOffType) {
          const timeOffTypeSummary: ITimeOffTypeSummary | undefined = this.findTimeOffTypeSummary(timeOffType.id);
          // should *always* be findable
          if (timeOffTypeSummary) {
            if (this.isUpcoming(tor) && this.isRequestedOrOnHold(tor)) {
              timeOffTypeSummary.requestedOnHold = {
                hours: timeOffTypeSummary.requestedOnHold.hours + rng.units,
                amount: timeOffTypeSummary.requestedOnHold.amount,
              };
            }
          }
        }
      }
    }
    this.filterTimeOffRequests();
    requestAnimationFrame(() => {
      const timeOffTypeSummaries = this.state.timeOffTypeSummaries;
      this.state.timeOffTypeSummaries = [];
      this.state.timeOffTypeSummaries = timeOffTypeSummaries;
    });
  }

  resetTimeOffTypeSummaries() {
    for (const summary of this.state.timeOffTypeSummaries) {
      const timeOffType: ITimeOffType | undefined = this.findTimeOffType(summary.id);

      // should *always* be findable
      if (timeOffType) {
        summary.resetWith(timeOffType);
      }
    }
  }

  deDupe(timeOffRequests: ITimeOffRequest[]): ITimeOffRequest[] {
    const results: ITimeOffRequest[] = [];
    for (const timeOffRequest of timeOffRequests) {
      const id = timeOffRequest.id;
      if (!results.find((result: ITimeOffRequest) => result.id === id)) {
        results.push(timeOffRequest);
      }
    }
    return results;
  }

  findTimeOffType(id: string): ITimeOffType | undefined {
    return this.state.timeOffTypes.find((tot: ITimeOffType) => tot.id === id);
  }

  findTimeOffTypeForReasonId(id: string): ITimeOffType | undefined {
    return this.state.timeOffTypes.find((tot: ITimeOffType) => tot.withdrawalReasons?.map((r: INameEntity) => r.id).includes(id));
  }

  findTimeOffTypeSummary(id: string): ITimeOffTypeSummary | undefined {
    return this.state.timeOffTypeSummaries.find((tots: ITimeOffTypeSummary) => tots.id === id);
  }

  isUpcoming(tor: ITimeOffRequest) {
    return tor.effective.end >= localTimeZoneUTCNow().startOfDay();
  }
  isPast(tor: ITimeOffRequest) {
    return tor.effective.end < localTimeZoneUTCNow().startOfDay();
  }

  filterTimeOffRequests() {
    this.state.upcomingTimeOffRequestsGrid.rows.set([]);
    this.state.pastTimeOffRequestsGrid.rows.set([]);
    if (!this.selectedTimeOffType) return;

    const timeOffRequests = this.state.timeOffRequests.filter((tor: ITimeOffRequest) =>
      this.filterOnMatchingReasonId(this.selectedTimeOffType!, tor),
    );
    if (!timeOffRequests.length) return;

    // get, sort (ascending), and convert the upcoming time off requests, then populate the grid
    const upcomingTimeOffRequests = timeOffRequests.filter(this.isUpcoming);
    upcomingTimeOffRequests.sort((a, b) => a.effective.start.utc - b.effective.start.utc);
    const upcomingRows = this.createTimeOffRequestRows(upcomingTimeOffRequests, this.state.upcomingTimeOffRequestsGrid.columns);
    this.state.upcomingTimeOffRequestsGrid.set(upcomingRows);

    // get, sort (descending), and convert the past time off requests, then populate the grid
    const pastTimeOffRequests = timeOffRequests.filter(this.isPast);
    pastTimeOffRequests.sort((a, b) => b.effective.start.utc - a.effective.start.utc);
    const pastRows = this.createTimeOffRequestRows(pastTimeOffRequests, this.state.pastTimeOffRequestsGrid.columns);
    this.state.pastTimeOffRequestsGrid.set(pastRows);
  }

  filterOnMatchingReasonId(timeOffType: ITimeOffType, timeOffRequest: ITimeOffRequest): boolean {
    let foundReason: boolean = false;
    if (timeOffRequest.timeOffRanges.length > 0) {
      for (const detail of timeOffRequest.timeOffRanges) {
        if (timeOffType.withdrawalReasons?.map((reason) => reason.id).includes(detail.reason.id)) foundReason = true;
      }
    }
    return foundReason;
  }

  addTransactionError = (message: IMessage) => {
    const row = this.transactionsGrid.rows.find((r: any) => r.data.id === message.id);
    if (row) {
      const error: IError = { text: message.text!, severity: ErrorSeverity.Error };
      row.cells['effective'].error = error;
    }
  };

  private doesTimeOffTypeHaveErrors = (timeOffType: ITimeOffType | undefined): boolean => {
    if (!timeOffType) return false;
    return timeOffType.transactions?.some((t: ITimeOffTypeTransaction) => t.messages?.some((m) => m.level === 'error')) || false;
  };

  get timeOffHasErrors(): boolean {
    return this.doesTimeOffTypeHaveErrors(this.state.selectedTimeOffType);
  }

  get available(): number | undefined {
    return this.state.selectedTimeOffType?.available?.hours;
  }

  addTimeOffTypeErrors = () => {
    for (const row of this.timeOffTypesGrid.rows) {
      const timeOffType = this.state.timeOffTypes.find((tot) => tot.id === row.data.id);
      // should always be found
      if (timeOffType) {
        if (this.doesTimeOffTypeHaveErrors(timeOffType)) {
          row.cells['name'].error = { text: 'at least 1 transaction has an error on it', severity: ErrorSeverity.Error };
        }
      }
    }
  };

  makeTimeOffTypeLink = (id: string) => {
    if (timeOffDialogModel.isOpen) return '';
    return `${Routes.Settings}/time_off_type?selectedItem=${id}`;
  };

  get transactionColumns(): any[] {
    return [
      { name: 'id', title: 'Id', visible: false },
      {
        name: 'effective',
        title: 'Effective',

        accessor: { getter: toRange },
        width: 150,
        validators: [validator(this.checkForMessages, field('id'))],
        renderer: CellRendererSelector,
        type: {
          type: 'text',
          options: {
            selector: (cell: ICell) => this.getEffectiveCellRenderer(cell),
            to: (cell: ICell) => this.makeTimecardLink(this.employeeId!, getEffectiveRange(cell)),
            click: (cell: ICell) =>
              this.handleEditAccrual(
                cell.row.data.id,
                this.employeeId!,
                this.selectedTimeOffType!.id,
                cell.row.cells.effective.value,
              ),
          },
        },
      },
      { name: 'units', title: 'Units', align: 'right', map: this.getUnits },
      { name: 'amount', title: 'Amount $', align: 'right', map: this.getAmount },
      { name: 'type', title: 'Type', map: safeCapitalCase },
      { name: 'source', title: 'Source', map: safeCapitalCase, width: 60 },
    ];
  }

  get timeOffTypeColumns(): any[] {
    return [
      { name: 'id', title: 'Id', visible: false },
      { name: 'name', title: 'Time Off Type', width: 125 },
      { name: 'earned', title: 'Earned', renderer: TimeOffTypeBalanceCell },
      { name: 'used', title: 'Used', renderer: TimeOffTypeBalanceCell },
      { name: 'planned', title: 'Planned', renderer: TimeOffTypeBalanceCell },
      { name: 'available', title: 'Available', renderer: TimeOffTypeBalanceCell },
      { name: 'requestedOnHold', title: 'Requested / On Hold', renderer: TimeOffTypeBalanceCell, cls: 'requestedOnHoldCell' },
    ];
  }

  get timeOffRequestColumns(): any[] {
    return [
      { name: 'id', title: 'Id', visible: false },
      {
        name: 'effective',
        title: 'Effective',
        map: toDateRange,
        width: 150,
        renderer: CellRendererSelector,
        type: {
          type: 'text',
          options: {
            selector: (cell: ICell) => this.getEffectiveTimeOffCellRenderer(cell),
            to: (cell: ICell) => this.makeTimeOffRequestLink(this.employeeId!, cell.row.cells.effective.value),
            click: (cell: ICell) => this.handleDisplayTimeOffRequest(cell.row.data.id),
          },
        },
      },
      { name: 'units', title: 'Units', align: 'right', map: this.getUnits },
      { name: 'amount', title: 'Amount $', align: 'right', map: this.getAmount },
      { name: 'reason', title: 'Reason' },
      { name: 'status', title: 'Status', map: safeCapitalCase },
    ];
  }

  getUnits = (units: number): string => {
    return this.selectedTimeOffType?.trackingUnits && units ? roundTo(units, 3).toString() : '';
  };

  getAmount = (amount: number): string => {
    return this.selectedTimeOffType?.trackingAmounts && amount ? `$${amount.toFixed(2)}` : '';
  };

  checkForMessages: IValidationFn = (value: any, rowId: string): IError | undefined => {
    const row: any = this.transactionsGrid.rows.find((row) => row.id === rowId);
    if (!row || !row.data.messages || !row.data.messages.length) return undefined;

    const errorMsg: string = row.data.messages
      .reduce((prev: string[], current: IMessage) => {
        prev.push(current.text!);
        return prev;
      }, [])
      .join('; ');
    const error = errorMsg ? { text: errorMsg, severity: ErrorSeverity.Error } : undefined;
    return error;
  };

  makeTimecardLink = (employeeId: string, effective: DateRange): string => {
    if (timeOffDialogModel.isOpen) return '';

    return payRouteManager.createPayPlanLinkFor({
      filters: { employeeIds: [employeeId] },
      effective: effective.toJSON(),
      selectedEmployeeId: employeeId,
    });
  };

  makeTimeOffRequestLink = (employeeId: string, effective: string): string => {
    if (timeOffDialogModel.isOpen) return '';
    return timeOffCalendarRouteManager.createLinkFor({ effective: effective, selectedEmployeeId: employeeId });
  };

  getEffectiveTimeOffCellRenderer(cell: ICell) {
    const type = cell.row.data.type;
    const isTimeOffRequest = 'partialDay' in cell.row.data;
    if (timeOffDialogModel.isOpen) {
      return <span>{cell.column.map?.(cell.value) ?? cell.value}</span>;
    }

    return type === 'accrual' || isTimeOffRequest ? <ActionCell cell={cell} /> : <LinkCell cell={cell} />;
  }

  getEffectiveCellRenderer(cell: ICell) {
    const type = cell.row.data.type;
    const isTimecard = cell.row.data.source === 'timecard';
    const isTimeOffRequest = 'partialDay' in cell.row.data;

    return (type === 'accrual' || isTimeOffRequest) && !isTimecard ? <ActionCell cell={cell} /> : <LinkCell cell={cell} />;
  }

  createColumns(columns: any[]): IColumn[] {
    const cols: IColumn[] = [];

    for (const col of columns) {
      const { name, title, ...opts } = col;
      const column = createColumn(name, title, opts);
      cols.push(column);
    }
    return cols;
  }

  getRowId(rowData: ITimeOffType | ITimeOffRequest | IRowData) {
    return rowData.id;
  }

  createTimeOffTypeRows(results: ITimeOffTypeSummary[] | undefined, _cols: IColumn[]): IRowData[] {
    const rows: IRowData[] = [];

    if (results) {
      for (const row of results) {
        const r: IRowData = { id: `${this.getRowId(row) || guid()}`, data: row, options: (row as any).options };
        rows.push(r);
      }
    }
    return rows;
  }

  createTransactionRows(results: ITimeOffTypeTransaction[] | undefined, _cols: IColumn[]): IRowData[] {
    const rows: IRowData[] = [];

    if (results) {
      for (const row of results) {
        const r: IRowData = { id: `${this.getRowId(row) || guid()}`, data: row, options: (row as any).options };
        rows.push(r);
      }
    }
    return rows;
  }

  isApprovedOrPublished = (tor: ITimeOffRequest): boolean => !!(tor.status && ['approved', 'published'].includes(tor.status));
  isRequestedOrOnHold = (tor: ITimeOffRequest): boolean => !!(tor.status && ['requested', 'onHold'].includes(tor.status));

  createTimeOffRequestRows(results: ITimeOffRequest[] | undefined, _cols: IColumn[]): IRowData[] {
    const rows: IRowData[] = [];

    if (results) {
      for (const row of results as any[]) {
        row.units = 0;
        row.amount = 0;
        row.reason = '';

        if (row.timeOffRanges.length === 1 && row.timeOffRanges[0]?.effective == null) {
          const range = row.timeOffRanges[0];
          if (this.isApprovedOrPublished(row)) {
            row.amount = range.amount;
          }
          row.units = range.units;
          row.reason = range.reason.name;
        } else {
          if (this.isApprovedOrPublished(row)) {
            row.amount = row.timeOffRanges[0].amount;
          }
          row.units = row.timeOffRanges.reduce((prev: number, curr: ITimeOffRange) => prev + curr.units, 0);
          row.reason = row.timeOffRanges.map((d: ITimeOffRange) => d.reason.name).join(', ');
        }

        const r: IRowData = { id: `${this.getRowId(row) || guid()}`, data: row, options: row.options };
        rows.push(r);
      }
    }
    return rows;
  }

  createOptions(): IGridOptions {
    return {
      clearSelectionOnBlur: false,
      readOnly: true,
      variableKeybinds: {
        'Ctrl+Insert': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
        'Ctrl+Delete': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
        'Ctrl+D': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
      },
    };
  }

  createGrid(columns: any[]): IGrid {
    const cols: IColumn[] = this.createColumns(columns);
    const rows: IRowData[] = this.createTimeOffTypeRows([], cols);
    const options: IGridOptions = this.createOptions();
    const grid: IGrid = createGrid(rows, cols, options);
    grid.rowHeight = 32;
    return grid;
  }

  refresh = debounce(() => this.loadTimeOffTypes(this.employeeId, undefined, true), 3000, { leading: true, trailing: false });

  handleAccrualDialogSubmit = () => {
    // refresh time off types to account for newly inserted / updated accruals
    this.refresh();
  };

  handleAccrualDialogRemove = () => {
    this.handleAccrualDialogSubmit();
  };

  handleTimeOffDialogSubmit = (result: ITimeOffRequest[] | undefined) => {
    // refresh time off requests to account for newly inserted requests
    if (result && result.length > 0) {
      this.mergeTimeOffRequests(result);
    }
    this.updateTimeOffTypeSummariesWithTimeOffRequests();
  };

  handleDeleteTimeOffRequests = (timeOffRequestIds: string[]) => {
    for (const id of timeOffRequestIds) {
      this.deleteTimeOffRequestForId(id);
    }
    this.updateTimeOffTypeSummariesWithTimeOffRequests();
  };

  mergeTimeOffRequests(timeOffRequests: ITimeOffRequest[]) {
    for (const timeOffRequest of timeOffRequests) {
      this.deleteTimeOffRequestForId(timeOffRequest.id);
      this.state.timeOffRequests.push(timeOffRequest);
    }
  }

  deleteTimeOffRequestForId(id: string) {
    const existingIndex: number = this.state.timeOffRequests.findIndex((tor: ITimeOffRequest) => tor.id === id);
    if (existingIndex !== -1) {
      this.state.timeOffRequests.splice(existingIndex, 1);
    }
  }

  get canAddAccrual(): boolean {
    return mainPageModel.canAddTimeOffAccruals && !!this.selectedTimeOffType;
  }

  get canEditAccrual(): boolean {
    return mainPageModel.canEditTimeOffAccruals;
  }

  handleAddAccrual = () => {
    accrualDialogModel.createAccrual(this.employeeId, this.selectedTimeOffType?.id);
  };

  handleEditAccrual = (accrualId: string, employeeId: string, timeOffTypeId: string, _effectiveRange: string) => {
    accrualDialogModel.editAccrual(accrualId, employeeId, timeOffTypeId);
  };

  handleDisplayTimeOffRequest = (id: string) => {
    if (timeOffDialogModel.isOpen) return;

    const timeOffRequest: ITimeOffRequest | undefined = this.state.timeOffRequests.find((t) => t.id === id);
    if (timeOffRequest === undefined) {
      return;
    }
    timeOffDialogModel.display(timeOffRequest);
  };
}

const timeOffBalancesDialogModel = new TimeOffBalancesDialogModel();
export default timeOffBalancesDialogModel;
